import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CONSUMER_AUTH_ROUTES, TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";

const MOCK_FULL_NAME = "Jane Doe";
const MOCK_EMAIL = "jane@email.com";

/** Gallery consumer identity — same person the session menu shows. */
export const GALLERY_CONSUMER = {
  fullName: MOCK_FULL_NAME,
  email: MOCK_EMAIL,
} as const;

function initialsFromUser(fullName: string, email: string): string {
  const name = fullName.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const local = email.split("@")[0]?.trim();
  if (local) return local.slice(0, 2).toUpperCase();
  return "?";
}

/**
 * Gallery-safe session chrome for fund-request / payout-claim transactions.
 * Visual match of the widget ConsumerSessionMenu — no Clerk. Sign-out jumps
 * to the Sign in gallery screen.
 */
export const ConsumerSessionMenu = memo(() => {
  const { t } = useTranslation("consumer-auth");
  const { theme } = useStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, background, bodyCopy } = colorConfig;
  const initials = initialsFromUser(MOCK_FULL_NAME, MOCK_EMAIL);
  const label = MOCK_FULL_NAME || t("accountMenu");

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="consumer-session-menu">
      <button
        type="button"
        className="consumer-session-menu__trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className="consumer-session-menu__initials"
          style={{ background: backgroundBtn, color: textColorBtn }}
          aria-hidden
        >
          {initials}
        </span>
      </button>
      {open && (
        <div
          className="consumer-session-menu__dropdown"
          role="menu"
          style={{ background, color: TITLE_COLOR }}
        >
          <div className="consumer-session-menu__identity">
            <p
              className="consumer-session-menu__name"
              style={{ color: TITLE_COLOR }}
            >
              {MOCK_FULL_NAME}
            </p>
            <p
              className="consumer-session-menu__email"
              style={{ color: bodyCopy }}
            >
              {MOCK_EMAIL}
            </p>
          </div>
          <button
            type="button"
            role="menuitem"
            className="consumer-session-menu__sign-out"
            style={{ color: TITLE_COLOR }}
            onClick={() => navigate(CONSUMER_AUTH_ROUTES.SIGN_IN_AFTER_LOGOUT)}
          >
            {t("signOut")}
          </button>
        </div>
      )}
    </div>
  );
});
